import { computed, ref } from 'vue'
import router from '@/router'

const DASHBOARD_FULL = '/dashboard'
const MAX_NON_AFFIX = 15

/** @typedef {{ fullPath: string, path: string, title: string, affix: boolean, routeName: string|null }} VisitedTag */

/** @type {import('vue').Ref<VisitedTag[]>} */
const visited = ref([])

function dashboardTag() {
  return {
    fullPath: DASHBOARD_FULL,
    path: DASHBOARD_FULL,
    title: '工作台',
    affix: true,
    routeName: 'dashboard',
  }
}

function ensureAffix() {
  // 历史版本可能重复注册了 /dashboard，清理非 affix 的「工作台」
  const hasDupDashboard = visited.value.some((t) => !t.affix && t.path === DASHBOARD_FULL)
  if (hasDupDashboard) {
    visited.value = visited.value.filter((t) => t.affix || t.path !== DASHBOARD_FULL)
  }
  if (!visited.value.some((t) => t.affix)) {
    visited.value = [dashboardTag(), ...visited.value]
  }
}

/** 纯 redirect、无组件的匹配不建标签 */
function shouldRegisterTag(route) {
  if (!route || route.meta?.public) return false
  const matched = route.matched || []
  if (!matched.length) return false
  const leaf = matched[matched.length - 1]
  if (!leaf.components?.default) return false
  const title = route.meta?.title
  if (!title || !String(title).trim()) return false
  return true
}

function pruneOverflow() {
  const nonAffix = visited.value.filter((t) => !t.affix)
  if (nonAffix.length <= MAX_NON_AFFIX) return
  let toRemove = nonAffix.length - MAX_NON_AFFIX
  const next = []
  for (const t of visited.value) {
    if (!t.affix && toRemove > 0) {
      toRemove -= 1
      continue
    }
    next.push(t)
  }
  visited.value = next
}

/** 同一页面（path / name 相同）仅保留一个标签；query 变化时更新 fullPath，避免重复页签 */
function isSamePageTag(tag, route) {
  if (!tag) return false
  if (route.path === DASHBOARD_FULL && (tag.affix || tag.path === DASHBOARD_FULL)) return true
  if (tag.affix) return false
  const routeName = route.name ? String(route.name) : null
  if (routeName && tag.routeName === routeName) return true
  return tag.path === route.path
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 */
function addView(route) {
  if (!shouldRegisterTag(route)) return
  ensureAffix()

  // 工作台仅保留左侧固定 affix 标签，避免登录后再多一条可关闭的「工作台」
  if (route.path === DASHBOARD_FULL) {
    const affixIdx = visited.value.findIndex((t) => t.affix)
    if (affixIdx >= 0) {
      const next = [...visited.value]
      next[affixIdx] = { ...dashboardTag(), fullPath: route.fullPath }
      visited.value = next
    }
    return
  }

  const newTag = {
    fullPath: route.fullPath,
    path: route.path,
    title: String(route.meta.title).trim(),
    affix: false,
    routeName: route.name ? String(route.name) : null,
  }

  const hitIdx = visited.value.findIndex((t) => isSamePageTag(t, route))
  if (hitIdx >= 0) {
    let replaced = false
    const next = []
    for (const t of visited.value) {
      if (isSamePageTag(t, route)) {
        if (!replaced) {
          next.push(newTag)
          replaced = true
        }
        continue
      }
      next.push(t)
    }
    visited.value = next
    return
  }

  visited.value.push(newTag)
  pruneOverflow()
}

/**
 * @param {VisitedTag} tag
 */
function closeTab(tag) {
  if (!tag || tag.affix) return
  const list = visited.value
  const index = list.findIndex((t) => t.fullPath === tag.fullPath)
  if (index < 0) return

  const isActive = router.currentRoute.value.fullPath === tag.fullPath
  const nextList = list.filter((t) => t.fullPath !== tag.fullPath)
  visited.value = nextList.length ? nextList : [dashboardTag()]

  if (!isActive) return

  const left = nextList[index - 1] || nextList[index] || dashboardTag()
  router.push(left.fullPath)
}

/**
 * @param {VisitedTag} tag
 */
function goTab(tag) {
  if (!tag?.fullPath) return
  if (router.currentRoute.value.fullPath === tag.fullPath) return
  router.push(tag.fullPath)
}

const activeFullPath = computed(() => router.currentRoute.value.fullPath)

function resetTagsView() {
  visited.value = []
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 */
function syncFromRoute(route) {
  if (!shouldRegisterTag(route)) return
  addView(route)
}

export function useTagsView() {
  return {
    visited,
    activeFullPath,
    ensureAffix,
    addView,
    syncFromRoute,
    closeTab,
    goTab,
    resetTagsView,
  }
}
